import React, { useState, useEffect, useRef } from 'react';
import { Table, Badge, Card, Row, Col, Form, Button, Spinner, ButtonGroup } from 'react-bootstrap';
import { RefreshCcw, AlertCircle, Info, ShieldAlert, Database, Activity, Download } from 'lucide-react';
import axiosInstance from '../../api/axiosConfig';

const SystemLogs = () => {
    const [logs,          setLogs]          = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [severityFilter,setSeverityFilter] = useState('ALL');
    const [logSource,     setLogSource]     = useState('live');
    const [isAutoRefresh, setIsAutoRefresh] = useState(false);
    const refreshTimer = useRef(null);

    const fetchLogs = async (showLoader = true) => {
        if (showLoader) setLoading(true);
        try {
            const res = await axiosInstance.get('/superadmin/logs', {
                params: { severity: severityFilter, source: logSource }
            });
            setLogs(res.data.data || []);
        } catch (err) {
            console.error('Failed to fetch logs:', err);
            setLogs([]);
        } finally {
            if (showLoader) setLoading(false);
        }
    };

    // FIX: details column was a JSON object — React cannot render objects directly.
    // Stringify it safely before rendering.
    const formatDetails = (details) => {
        if (!details) return '-';
        if (typeof details === 'string') return details;
        try {
            return JSON.stringify(details);
        } catch {
            return String(details);
        }
    };

    const exportToCSV = () => {
        if (logs.length === 0) { alert('No logs to export'); return; }
        const headers  = ['ID', 'Timestamp', 'Severity', 'Module', 'Message', 'Details'];
        const csvRows  = logs.map(log => [
            log.id,
            `"${new Date(log.created_at).toLocaleString()}"`,
            log.severity || log.level || 'INFO',
            log.module   || '',
            `"${(log.message || '').replace(/"/g, '""')}"`,
            `"${formatDetails(log.details).replace(/"/g, '""')}"`
        ]);
        const csv  = [headers, ...csvRows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `peoplesdock_logs_${new Date().toISOString().slice(0,10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    useEffect(() => {
        fetchLogs();
        if (logSource === 'archive') setIsAutoRefresh(false);
    }, [severityFilter, logSource]);

    useEffect(() => {
        if (isAutoRefresh && logSource === 'live') {
            refreshTimer.current = setInterval(() => fetchLogs(false), 30000);
        } else {
            clearInterval(refreshTimer.current);
        }
        return () => clearInterval(refreshTimer.current);
    }, [isAutoRefresh, logSource, severityFilter]);

    const getSeverityBadge = (severity) => {
        const level = (severity || 'INFO').toUpperCase();
        if (level === 'ERROR'    || level === 'CRITICAL') return <Badge bg="danger">{level}</Badge>;
        if (level === 'WARN'     || level === 'WARNING')  return <Badge bg="warning" text="dark">{level}</Badge>;
        return <Badge bg="info">INFO</Badge>;
    };

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white py-3 border-bottom">
                <Row className="align-items-center g-2">
                    <Col>
                        <h5 className="mb-0 fw-bold">System Logs</h5>
                        <small className="text-muted">
                            {logSource.toUpperCase()} · {logs.length} entries
                            {isAutoRefresh && <Badge bg="success" className="ms-2">Auto-refresh</Badge>}
                        </small>
                    </Col>
                    <Col xs="auto" className="d-flex align-items-center gap-2 flex-wrap">
                        <Button variant="outline-success" size="sm" onClick={exportToCSV}>
                            <Download size={14} className="me-1" /> Export CSV
                        </Button>

                        <ButtonGroup size="sm">
                            <Button
                                variant={logSource === 'live' ? 'primary' : 'outline-primary'}
                                onClick={() => setLogSource('live')}
                            >
                                <Activity size={14} className="me-1" />Live
                            </Button>
                            <Button
                                variant={logSource === 'archive' ? 'secondary' : 'outline-secondary'}
                                onClick={() => setLogSource('archive')}
                            >
                                <Database size={14} className="me-1" />Archive
                            </Button>
                        </ButtonGroup>

                        {logSource === 'live' && (
                            <Form.Check
                                type="switch"
                                id="auto-refresh"
                                label="Auto"
                                checked={isAutoRefresh}
                                onChange={e => setIsAutoRefresh(e.target.checked)}
                                className="small text-muted mb-0"
                            />
                        )}

                        <Form.Select
                            size="sm"
                            style={{ width: 130 }}
                            value={severityFilter}
                            onChange={e => setSeverityFilter(e.target.value)}
                        >
                            <option value="ALL">All Levels</option>
                            <option value="ERROR">Errors</option>
                            <option value="WARN">Warnings</option>
                            <option value="INFO">Info</option>
                        </Form.Select>

                        <Button variant="outline-primary" size="sm" onClick={() => fetchLogs(true)}>
                            <RefreshCcw size={14} className={loading ? 'spin-icon' : ''} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>

            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <Table hover responsive className="mb-0 align-middle small">
                        <thead className="bg-light text-uppercase" style={{ fontSize: 11 }}>
                            <tr>
                                <th className="ps-4" style={{ width: 170 }}>Timestamp</th>
                                <th style={{ width: 90 }}>Severity</th>
                                <th style={{ width: 110 }}>Module</th>
                                <th>Message</th>
                                <th style={{ maxWidth: 200 }}>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length > 0 ? logs.map(log => (
                                <tr key={log.id}>
                                    <td className="ps-4 text-muted">
                                        {new Date(log.created_at).toLocaleString('en-IN')}
                                    </td>
                                    <td>{getSeverityBadge(log.severity || log.level)}</td>
                                    <td>
                                        <code className="text-primary small">{log.module || '-'}</code>
                                    </td>
                                    <td className="fw-medium">{log.message}</td>
                                    {/* FIX: Use formatDetails() — never render raw objects */}
                                    <td
                                        className="text-muted"
                                        style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                        title={formatDetails(log.details)}
                                    >
                                        {formatDetails(log.details)}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="text-center py-5 text-muted">
                                        No logs found for the selected criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
        </Card>
    );
};

export default SystemLogs;
